import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useConversations, type ConvSummary } from '@/hooks/useMessenger';

interface ConvListProps {
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** True when mounted inside another list-pane wrapper (skip own <aside>) */
  embedded?: boolean;
}

export function ConvList({ selectedId, onSelect, embedded = false }: ConvListProps) {
  const { t } = useTranslation();
  const { data: convs = [] } = useConversations();
  const [q, setQ] = useState('');

  const filtered = q
    ? convs.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()))
    : convs;

  const Wrapper = embedded
    ? ({ children }: { children: ReactNode }) => <>{children}</>
    : ({ children }: { children: ReactNode }) => <aside className="msg-list-pane">{children}</aside>;

  return (
    <Wrapper>
      <div className="msg-search-wrap">
        <div className="msg-search-input-rel">
          <span className="material-symbols-outlined">search</span>
          <input
            className="msg-search-input"
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('messenger.search_placeholder')}
          />
        </div>
      </div>
      <div className="msg-conv-list">
        {filtered.map((c) => (
          <ConvItem
            key={c.id}
            conv={c}
            active={selectedId === c.id}
            onClick={() => onSelect(c.id)}
          />
        ))}
      </div>
    </Wrapper>
  );
}

function ConvItem({
  conv,
  active,
  onClick,
}: {
  conv: ConvSummary;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <div
      className={conv.unread > 0 ? 'conv-item unread' : 'conv-item'}
      data-active={active || undefined}
      onClick={onClick}
      role="button"
    >
      <div className="conv-avatar" style={{ background: conv.avatarGradient }}>
        {conv.initials}
        <span className="conv-online-dot" data-state={conv.online} />
      </div>
      <div className="conv-body">
        <div className="conv-top">
          <div className="conv-name">{conv.name}</div>
          <div className="conv-time">{conv.lastTime}</div>
        </div>
        <div className="conv-bottom">
          <div className="conv-preview">{conv.lastMessage}</div>
          {conv.unread > 0 && <span className="conv-badge">{conv.unread}</span>}
        </div>
      </div>
    </div>
  );
}
